import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import RegistrationModal from '../components/RegistrationModal';
import PartnerForm from '../components/PartnerForm';
import EventModal from '../components/EventModal';
import { getPlayersByTournament, getPartnersByTournament, getTournamentEvents } from '../lib/supabase';

interface DashboardHomeProps {
  tournamentId: string;
}

interface DashboardStats {
  players: {
    total: number;
    registered: number;
    pending: number;
    international: number;
  };
  partners: {
    total: number;
    value: number;
    active: number;
    pending: number;
  };
  events: {
    total: number;
    upcoming: number;
    completed: number;
  };
}

const defaultStats: DashboardStats = {
  players: { total: 0, registered: 0, pending: 0, international: 0 },
  partners: { total: 0, value: 0, active: 0, pending: 0 },
  events: { total: 0, upcoming: 0, completed: 0 }
};

function DashboardHome({ tournamentId }: DashboardHomeProps) {
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [loading, setLoading] = useState(true);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [playerRegistrationRevenue, setPlayerRegistrationRevenue] = useState(0);

  useEffect(() => {
    if (tournamentId) {
      fetchDashboardData();
    }
  }, [tournamentId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      await fetchPlayerRegistrationRevenue();

      const { data: playersData, error: playersError } = await getPlayersByTournament(tournamentId);
      if (playersError) throw playersError;

      const { data: partnersData, error: partnersError } = await getPartnersByTournament(tournamentId);
      if (partnersError) throw partnersError;

      const { data: eventsData, error: eventsError } = await getTournamentEvents(tournamentId);
      if (eventsError) throw eventsError;

      const totalPlayers = playersData?.length || 0;
      const registeredPlayers = playersData?.filter(p => p.status?.toLowerCase() === 'active').length || 0;
      const pendingPlayers = playersData?.filter(p => p.status?.toLowerCase() === 'pending').length || 0;
      const internationalPlayers = playersData?.filter(p => p.country && p.country.toLowerCase() !== 'nigeria').length || 0;

      const totalPartners = partnersData?.length || 0;
      const activePartners = partnersData?.filter(p => p.status === 'active').length || 0;
      const pendingPartners = partnersData?.filter(p => p.status === 'pending').length || 0;
      const partnerValue = partnersData?.reduce((sum, p) => sum + (p.contribution || 0), 0) || 0;

      const totalEvents = eventsData?.length || 0;
      const now = new Date();
      const upcomingEvents = eventsData?.filter(e => new Date(e.date) > now).length || 0;
      const completedEvents = eventsData?.filter(e => new Date(e.date) <= now).length || 0;

      setStats({
        players: { total: totalPlayers, registered: registeredPlayers, pending: pendingPlayers, international: internationalPlayers },
        partners: { total: totalPartners, value: partnerValue, active: activePartners, pending: pendingPartners },
        events: { total: totalEvents, upcoming: upcomingEvents, completed: completedEvents }
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerRegistrationRevenue = async () => {
    try {
      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('registration_fee')
        .eq('id', tournamentId)
        .maybeSingle();

      if (tournamentError) throw tournamentError;

      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id, registration_status')
        .eq('tournament_id', tournamentId);

      if (playersError) throw playersError;

      const registrationFee = tournament?.registration_fee || 50000;
      const confirmedPlayers = players?.filter(p => {
        const rs = p.registration_status as { payment?: boolean } | null;
        return rs?.payment === true;
      }).length || 0;
      setPlayerRegistrationRevenue(registrationFee * confirmedPlayers);
    } catch (err) {
      console.error('Error calculating player registration revenue:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-blue-500/10 p-3 rounded-xl">
              <UserGroupIcon className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Players</p>
              <div className="flex items-center gap-2">
                <CountUp end={stats.players.total} duration={2} className="text-2xl font-bold text-golf-gray" />
                <span className="text-sm text-gray-500">({stats.players.registered} registered)</span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.players.pending}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">International</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.players.international}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-teal-500/10 p-3 rounded-xl">
              <BuildingOfficeIcon className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Partners</p>
              <div className="flex items-center gap-2">
                <CountUp end={stats.partners.total} duration={2} className="text-2xl font-bold text-golf-gray" />
                <span className="text-sm text-gray-500">({stats.partners.active} active)</span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Value</div>
              <div className="text-lg font-semibold text-golf-gray">
                {stats.partners.value > 0 ? `\u20A6${(stats.partners.value / 1000000).toFixed(1)}M` : '\u20A60'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.partners.pending}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-orange-500/10 p-3 rounded-xl">
              <CalendarIcon className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Events</p>
              <div className="flex items-center gap-2">
                <CountUp end={stats.events.total} duration={2} className="text-2xl font-bold text-golf-gray" />
                <span className="text-sm text-gray-500">total</span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Upcoming</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.events.upcoming}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.events.completed}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-green-500/10 p-3 rounded-xl">
              <ChartBarIcon className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Registration Revenue</p>
              <div className="text-2xl font-bold text-golf-gray">
                {playerRegistrationRevenue > 0
                  ? new Intl.NumberFormat('en-NG', {
                      style: 'currency',
                      currency: 'NGN',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(playerRegistrationRevenue)
                  : '\u20A60'}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Confirmed Players</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.players.registered}</div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:border-golf-orange transition-colors text-left"
          onClick={() => setIsPlayerModalOpen(true)}
        >
          <div className="flex items-center space-x-4">
            <div className="bg-golf-orange/10 p-3 rounded-xl">
              <UserGroupIcon className="w-6 h-6 text-golf-orange" />
            </div>
            <div>
              <p className="font-medium text-golf-gray">Add Player</p>
              <p className="text-sm text-gray-500">Register a new player</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:border-golf-orange transition-colors text-left"
          onClick={() => setIsPartnerModalOpen(true)}
        >
          <div className="flex items-center space-x-4">
            <div className="bg-golf-orange/10 p-3 rounded-xl">
              <BuildingOfficeIcon className="w-6 h-6 text-golf-orange" />
            </div>
            <div>
              <p className="font-medium text-golf-gray">Add Partner</p>
              <p className="text-sm text-gray-500">Create new partnership</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:border-golf-orange transition-colors text-left"
          onClick={() => setIsEventModalOpen(true)}
        >
          <div className="flex items-center space-x-4">
            <div className="bg-golf-orange/10 p-3 rounded-xl">
              <CalendarIcon className="w-6 h-6 text-golf-orange" />
            </div>
            <div>
              <p className="font-medium text-golf-gray">Schedule Event</p>
              <p className="text-sm text-gray-500">Plan tournament events</p>
            </div>
          </div>
        </motion.button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-golf-gray">Recent Activity</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <UserGroupIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-golf-gray">
                  {stats.players.total > 0
                    ? `${stats.players.total} players registered for the tournament`
                    : 'No players registered yet'}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.players.pending > 0
                    ? `${stats.players.pending} pending approvals`
                    : 'All registrations processed'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-teal-100 p-2 rounded-lg">
                <BuildingOfficeIcon className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-golf-gray">
                  {stats.partners.total > 0
                    ? `${stats.partners.total} partners supporting the tournament`
                    : 'No partners added yet'}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.partners.pending > 0
                    ? `${stats.partners.pending} pending partnership requests`
                    : 'All partnerships confirmed'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-golf-gray">
                  {stats.events.total > 0
                    ? `${stats.events.total} events scheduled`
                    : 'No events scheduled yet'}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.events.upcoming > 0
                    ? `${stats.events.upcoming} upcoming events`
                    : 'No upcoming events'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <RegistrationModal
        isOpen={isPlayerModalOpen}
        onClose={() => {
          setIsPlayerModalOpen(false);
          fetchDashboardData();
        }}
      />

      <PartnerForm
        isOpen={isPartnerModalOpen}
        onClose={() => {
          setIsPartnerModalOpen(false);
          fetchDashboardData();
        }}
      />

      {tournamentId && (
        <EventModal
          isOpen={isEventModalOpen}
          onClose={() => {
            setIsEventModalOpen(false);
            fetchDashboardData();
          }}
          tournamentId={tournamentId}
          event={null}
        />
      )}
    </div>
  );
}

export default DashboardHome;
